import { useEffect, useState, useCallback, useMemo } from "react";
import api from "@/lib/api";
import { ScrollText, RefreshCw, Filter, X, ChevronLeft, ChevronRight, Eye } from "lucide-react";
import PanelState, { extractApiError } from "@/components/PanelState";

const PAGE_SIZE = 25;

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString([], {
      month: "short", day: "numeric", year: "numeric",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
    });
  } catch { return iso; }
}

// Pretty pill tone per event_type family (fallback to grey)
function eventTone(et) {
  if (!et) return "bg-[#f5f6f8] text-[#4b5563] border-[#e7e9ee]";
  if (et.startsWith("user.")) return "bg-[#e7f4fb] text-[#1c6a99] border-[#c5e0ee]";
  if (et.startsWith("lead.")) return "bg-[#fff4d6] text-[#8a5b00] border-[#f3d97a]";
  if (et.startsWith("tenant.")) return "bg-[#fdecec] text-[#a12b2b] border-[#f5c5c5]";
  if (et.startsWith("document.")) return "bg-[#f3ecfb] text-[#5a2c8f] border-[#dccaf1]";
  return "bg-[#f5f6f8] text-[#4b5563] border-[#e7e9ee]";
}

// Best-effort target label from arbitrary audit rows (schema varies per event)
function targetLabel(row) {
  return (
    row.target_email ||
    row.tenant_slug ||
    row.business_name ||
    row.target_id ||
    row.collection ||
    "—"
  );
}

export default function AdminAuditLog() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loadState, setLoadState] = useState({ status: "loading", error: null });
  const [refreshing, setRefreshing] = useState(false);
  const [eventTypes, setEventTypes] = useState([]);
  const [actors, setActors] = useState([]);

  // filters
  const [eventType, setEventType] = useState("");
  const [actor, setActor] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [page, setPage] = useState(0);

  // drill-down modal
  const [detailRow, setDetailRow] = useState(null);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const params = { limit: PAGE_SIZE, offset: page * PAGE_SIZE };
      if (eventType) params.event_type = eventType;
      if (actor) params.actor_email = actor;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      const { data } = await api.get("/admin/audit-log", { params });
      setRows(Array.isArray(data?.items) ? data.items : []);
      setTotal(data?.total || 0);
      setLoadState({ status: "ok", error: null });
    } catch (err) {
      setRows([]);
      setTotal(0);
      setLoadState({ status: "error", error: extractApiError(err) });
    } finally {
      setRefreshing(false);
    }
  }, [eventType, actor, startDate, endDate, page]);

  // Load filter dropdown values once
  useEffect(() => {
    (async () => {
      try {
        const [et, ac] = await Promise.all([
          api.get("/admin/audit-log/event-types"),
          api.get("/admin/audit-log/actors"),
        ]);
        setEventTypes(et.data?.event_types || []);
        setActors(ac.data?.actors || []);
      } catch { /* silent — dropdowns fall back to text-only */ }
    })();
  }, []);

  useEffect(() => { load(); }, [load]);

  // Any filter change resets to page 0
  const applyFilter = (setter) => (value) => { setter(value); setPage(0); };

  const clearFilters = () => {
    setEventType(""); setActor(""); setStartDate(""); setEndDate(""); setPage(0);
  };

  const hasFilters = Boolean(eventType || actor || startDate || endDate);
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const rangeStart = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const rangeEnd = Math.min(total, (page + 1) * PAGE_SIZE);

  return (
    <div data-testid="admin-audit-log-page" className="space-y-5">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-semibold mb-2">
            Platform Admin
          </div>
          <h1 className="font-display font-bold text-[26px] sm:text-[32px] tracking-tight text-[#0e1420] inline-flex items-center gap-2.5">
            <ScrollText size={22} strokeWidth={1.6} className="text-[#0072bc]" />
            Audit Log
          </h1>
          <p className="text-[13.5px] text-[color:var(--cc-ink-muted)] mt-1">
            Every admin-side mutation (deletions, role changes, lead status transitions) with full before/after snapshots.
          </p>
        </div>
        <button
          onClick={load}
          disabled={refreshing}
          data-testid="admin-audit-log-refresh"
          className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold px-3 py-2 rounded-md border border-[#e7e9ee] text-[#0e1420] hover:bg-[#f5f6f8] disabled:opacity-50"
        >
          <RefreshCw size={13} strokeWidth={1.8} className={refreshing ? "animate-spin" : ""} />
          Refresh
        </button>
      </header>

      {/* Filter bar */}
      <div className="bg-white border border-[#e7e9ee] rounded-xl p-3 sm:p-4" data-testid="admin-audit-log-filters">
        <div className="flex items-start gap-3 flex-wrap">
          <div className="inline-flex items-center gap-1.5 text-[11.5px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-semibold pt-2 shrink-0">
            <Filter size={13} strokeWidth={1.8} />
            Filters
          </div>

          <FilterField label="Event type">
            <select
              value={eventType}
              onChange={(e) => applyFilter(setEventType)(e.target.value)}
              data-testid="filter-event-type"
              className="text-[12.5px] border border-[#e7e9ee] rounded-md px-2 py-1.5 pr-7 bg-white min-w-[180px]"
            >
              <option value="">All events</option>
              {eventTypes.map((et) => <option key={et} value={et}>{et}</option>)}
            </select>
          </FilterField>

          <FilterField label="Actor">
            <select
              value={actor}
              onChange={(e) => applyFilter(setActor)(e.target.value)}
              data-testid="filter-actor"
              className="text-[12.5px] border border-[#e7e9ee] rounded-md px-2 py-1.5 pr-7 bg-white min-w-[220px]"
            >
              <option value="">All actors</option>
              {actors.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </FilterField>

          <FilterField label="From">
            <input
              type="date"
              value={startDate}
              onChange={(e) => applyFilter(setStartDate)(e.target.value)}
              data-testid="filter-start-date"
              className="text-[12.5px] border border-[#e7e9ee] rounded-md px-2 py-1.5 bg-white"
            />
          </FilterField>

          <FilterField label="To">
            <input
              type="date"
              value={endDate}
              onChange={(e) => applyFilter(setEndDate)(e.target.value)}
              data-testid="filter-end-date"
              className="text-[12.5px] border border-[#e7e9ee] rounded-md px-2 py-1.5 bg-white"
            />
          </FilterField>

          {hasFilters && (
            <button
              onClick={clearFilters}
              data-testid="filter-clear"
              className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-[#0072bc] hover:text-[#00568f] pt-2 self-start"
            >
              <X size={12} strokeWidth={2} /> Clear
            </button>
          )}
        </div>
      </div>

      <PanelState
        status={loadState.status}
        error={loadState.error}
        isEmpty={rows.length === 0}
        onRetry={load}
        emptyTitle={hasFilters ? "No entries match these filters." : "No audit entries yet."}
        emptyBody={hasFilters
          ? "Try removing filters or widening the date range."
          : "Once an admin performs a mutation, entries will appear here."}
        testIdPrefix="admin-audit-log"
      />

      {loadState.status === "ok" && rows.length > 0 && (
        <>
          <div className="bg-white border border-[#e7e9ee] rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full text-[13px]">
                <thead className="bg-[#fafbfc] text-[10.5px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-semibold">
                  <tr>
                    <th className="text-left px-4 py-2.5 whitespace-nowrap">When</th>
                    <th className="text-left px-4 py-2.5 whitespace-nowrap">Event</th>
                    <th className="text-left px-4 py-2.5">Actor</th>
                    <th className="text-left px-4 py-2.5">Target</th>
                    <th className="text-left px-4 py-2.5">Reason / Note</th>
                    <th className="text-right px-4 py-2.5 whitespace-nowrap">Details</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#eef1f6]">
                  {rows.map((r) => (
                    <tr key={r.id || `${r.event_type}-${r.created_at}`} data-testid={`audit-row-${r.id}`}>
                      <td className="px-4 py-2.5 whitespace-nowrap font-mono-data text-[11.5px] text-[#0e1420]">
                        {fmtDateTime(r.created_at)}
                      </td>
                      <td className="px-4 py-2.5 whitespace-nowrap">
                        <span className={`text-[10.5px] font-mono-data uppercase tracking-widest border rounded px-2 py-0.5 ${eventTone(r.event_type)}`}>
                          {r.event_type}
                        </span>
                      </td>
                      <td className="px-4 py-2.5">
                        <div className="text-[12.5px] text-[#0e1420]">{r.actor_email || "—"}</div>
                      </td>
                      <td className="px-4 py-2.5">
                        <div className="text-[12.5px] text-[#0e1420] break-all">{targetLabel(r)}</div>
                        {r.collection && (
                          <div className="text-[10.5px] font-mono-data text-[color:var(--cc-ink-muted)] mt-0.5">
                            {r.collection}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-2.5 max-w-[280px]">
                        <div className="text-[12px] text-[color:var(--cc-ink-muted)] line-clamp-2">
                          {r.reason || "—"}
                        </div>
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        <button
                          onClick={() => setDetailRow(r)}
                          data-testid={`audit-row-view-${r.id}`}
                          className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-[#0072bc] hover:text-[#00568f]"
                        >
                          <Eye size={12} strokeWidth={1.8} /> View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between text-[12px] text-[color:var(--cc-ink-muted)]" data-testid="admin-audit-log-pagination">
            <div>
              Showing <span className="font-mono-data text-[#0e1420]">{rangeStart}–{rangeEnd}</span> of{" "}
              <span className="font-mono-data text-[#0e1420]">{total}</span>
            </div>
            <div className="inline-flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                data-testid="pagination-prev"
                className="inline-flex items-center gap-1 text-[12px] px-2.5 py-1.5 rounded-md border border-[#e7e9ee] text-[#0e1420] hover:bg-[#f5f6f8] disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronLeft size={13} strokeWidth={1.8} /> Prev
              </button>
              <span className="font-mono-data text-[11.5px] text-[#0e1420]" data-testid="pagination-status">
                {page + 1} / {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                data-testid="pagination-next"
                className="inline-flex items-center gap-1 text-[12px] px-2.5 py-1.5 rounded-md border border-[#e7e9ee] text-[#0e1420] hover:bg-[#f5f6f8] disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Next <ChevronRight size={13} strokeWidth={1.8} />
              </button>
            </div>
          </div>
        </>
      )}

      {detailRow && <DetailModal row={detailRow} onClose={() => setDetailRow(null)} />}
    </div>
  );
}

function FilterField({ label, children }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[10.5px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-semibold">
        {label}
      </span>
      {children}
    </label>
  );
}

function DetailModal({ row, onClose }) {
  const pretty = useMemo(() => {
    try { return JSON.stringify(row, null, 2); } catch { return String(row); }
  }, [row]);

  const copy = async () => {
    try { await navigator.clipboard.writeText(pretty); } catch { /* ignore */ }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center pt-16 px-4 pb-4"
      onClick={onClose}
      data-testid="audit-detail-backdrop"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[calc(100vh-8rem)] flex flex-col"
        data-testid="audit-detail-modal"
      >
        <div className="px-5 py-4 border-b border-[#eef1f6] flex items-start justify-between gap-3">
          <div>
            <div className="text-[10.5px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-semibold">
              Audit entry
            </div>
            <div className="mt-1 inline-flex items-center gap-2 flex-wrap">
              <span className={`text-[10.5px] font-mono-data uppercase tracking-widest border rounded px-2 py-0.5 ${eventTone(row.event_type)}`}>
                {row.event_type}
              </span>
              <span className="text-[12px] font-mono-data text-[color:var(--cc-ink-muted)]">
                {fmtDateTime(row.created_at)}
              </span>
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="audit-detail-close"
            className="p-1.5 rounded-md hover:bg-[#f5f6f8] text-[color:var(--cc-ink-muted)]"
            aria-label="Close"
          >
            <X size={16} strokeWidth={1.8} />
          </button>
        </div>
        <div className="px-5 py-4 flex-1 overflow-y-auto">
          <pre
            data-testid="audit-detail-json"
            className="bg-[#0e1420] text-[#e6edf3] text-[11.5px] font-mono-data rounded-lg p-4 overflow-x-auto leading-relaxed whitespace-pre"
          >
            {pretty}
          </pre>
        </div>
        <div className="px-5 py-3 border-t border-[#eef1f6] flex items-center justify-end gap-2">
          <button
            onClick={copy}
            data-testid="audit-detail-copy"
            className="text-[12px] font-semibold px-3 py-1.5 rounded-md border border-[#e7e9ee] text-[#0e1420] hover:bg-[#f5f6f8]"
          >
            Copy JSON
          </button>
          <button
            onClick={onClose}
            className="text-[12px] font-semibold px-3 py-1.5 rounded-md bg-[#0072bc] text-white hover:bg-[#00568f]"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
